// How long to keep waiting for a hash target that has not rendered yet.
const TARGET_WAIT_MS = 2000;

// Anything the reader does to move the page themselves wins over a jump we are
// still waiting to perform. Plain "scroll" would match our own animation.
const ABORT_EVENTS = ["wheel", "touchmove", "keydown"];

// Anchor ids are authored by editors in Contentful and fragments also arrive
// from third-party redirects, so the value is not safe to hand to
// querySelector - a leading digit, a space or a "/" throws SyntaxError. A
// malformed escape such as "#%" is no better behaved for the decode itself.
const findTarget = hash => {
    const raw = hash.replace(/^#/, "");

    let id;
    try {
        id = decodeURIComponent(raw);
    } catch {
        id = raw;
    }

    return id ? document.getElementById(id) : null;
};

const jumpTo = (target, behavior) => {
    target.scrollIntoView({ behavior });
    // Keyboard focus follows the jump, but must not fight the animation - a
    // bare focus() scrolls the element into view synchronously and would cut a
    // smooth scroll short. Sections opt in with tabIndex={-1}; anything else
    // ignores this.
    target.focus({ preventScroll: true });
};

let cancelPendingJump = null;

// Page sections are lazy-loaded (see DynamicChildren). They are in the static
// HTML, so a cold load finds them, but a client-side route change swaps in a
// page whose section chunks have not resolved yet - the element a hash names
// is simply absent for a beat. Giving up at that point is what makes in-page
// links look dead, so watch for the target instead of scrolling nowhere.
const waitForTarget = (hash, behavior) => {
    let timer = null;

    const settle = () => {
        clearTimeout(timer);
        observer.disconnect();
        ABORT_EVENTS.forEach(name => window.removeEventListener(name, settle));
        cancelPendingJump = null;
    };

    const observer = new MutationObserver(() => {
        const target = findTarget(hash);

        if (!target) {
            return;
        }

        settle();
        jumpTo(target, behavior);
    });

    timer = setTimeout(settle, TARGET_WAIT_MS);
    observer.observe(document.body, { childList: true, subtree: true });
    ABORT_EVENTS.forEach(name =>
        window.addEventListener(name, settle, { passive: true }),
    );

    cancelPendingJump = settle;
};

/**
 * Scroll the element a URL fragment names into view, waiting for it to mount
 * when it has not rendered yet.
 * @param {string} hash - Fragment to scroll to, leading "#" included.
 * @param {ScrollBehavior} behavior - Passed through to scrollIntoView.
 * @returns {boolean} False when the fragment names nothing to scroll to.
 */
const scrollToHash = (hash, behavior) => {
    cancelPendingJump?.();

    if (!hash || hash === "#") {
        return false;
    }

    const target = findTarget(hash);

    if (target) {
        jumpTo(target, behavior);
    } else {
        waitForTarget(hash, behavior);
    }

    return true;
};

export default scrollToHash;
